// === ZAGX v2 — Smart Render Engine ===
// Extracted from monolith lines 5926-5989
// Dirty-flag based rendering with error boundary and debouncing

import { fixMobileLayout, refreshScrollFades } from '../ui/layout.js';
import { renderDash, renderActionBell } from '../modules/dashboard.js';
import { renderP } from '../modules/portfolio.js';
import { renderM } from '../modules/monthly.js';
import { renderT } from '../modules/trades.js';
import { renderC } from '../modules/cash.js';
import { renderCF } from '../modules/cashflow.js';
import { renderLog } from '../modules/history.js';
import { renderGoals } from '../modules/goals.js';
import { renderSig } from '../modules/signals.js';
import { renderRisk } from '../modules/risk.js';
import { renderBonus } from '../modules/bonus.js';
import { renderAdvisory } from '../modules/advisory.js';
import { renderJournal } from '../modules/trade-journal-page.js';
import { renderLedgerPage } from '../modules/alpha-ledger-page.js';
import { renderTax } from '../modules/tax.js';
import { renderIntegrity } from '../modules/integrity-page.js';

// Error boundary wrapper — isolates module render failures
let _renderErrors = [];
function safeRender(name, fn) {
  try { fn(); } catch (e) {
    console.error('[ZAGX] ' + name + ' render failed:', e);
    _renderErrors.push({ module: name, error: e.message, ts: Date.now() });
    // Persistent ring buffer (last 30) — _renderErrors resets every pass, which made
    // transient failures invisible after the fact (blank-#signals hunt, 2026-07-14).
    // Inspect via window.__renderFailLog. Never cleared, never persisted to state.
    if (typeof window !== 'undefined') {
      (window.__renderFailLog = window.__renderFailLog || []).push({ module: name, error: e.message, stack: (e.stack || '').split('\n').slice(0, 4).join(' | '), ts: new Date().toISOString() });
      if (window.__renderFailLog.length > 30) window.__renderFailLog.shift();
    }
  }
}

// === SMART RENDER ENGINE (v0.7.0) ===
// Dirty flags: only re-render sections that actually changed
const _dirty = { dash: true, port: true, monthly: true, trades: true, cash: true, cf: true, log: true, goals: true, signals: true, risk: true, bonus: true, advisory: true, journal: true, ledger: true, tax: true, integrity: true };

function markDirty(...sections) {
  if (!sections.length) { Object.keys(_dirty).forEach(k => _dirty[k] = true); return; }
  sections.forEach(s => { if (s in _dirty) _dirty[s] = true; });
  // Journal derives entirely from trades/realized — trades dirty implies journal dirty.
  if (sections.includes('trades')) _dirty.journal = true;
  // Alpha Ledger derives from advisory rec lifecycles — advisory dirty implies ledger dirty.
  if (sections.includes('advisory')) _dirty.ledger = true;
  // Cash reads the cashflow month through projectedCashTRY (the TRY line in the Cash
  // Transactions header), so a cashflow edit changes a number on the Cash page. Observed
  // 2026-08-08: raising August's portfolio commitment to ₺168,405 left Cash showing the
  // ₺113,832 projection until a reload. The implication belongs HERE and not in saveCFMo,
  // because every future cashflow writer inherits it — a rule with one home, not N callers.
  if (sections.includes('cf')) _dirty.cash = true;
}

// Map of section → render function
const _renderMap = {
  dash: ['Dashboard', renderDash],
  port: ['Portfolio', renderP],
  monthly: ['Monthly', renderM],
  trades: ['Trades', renderT],
  cash: ['Cash', renderC],
  cf: ['Income & Expenses', renderCF],
  goals: ['Goals', renderGoals],
  signals: ['Signals', renderSig],
  risk: ['Risk', renderRisk],
  bonus: ['Bonus', renderBonus],
  log: ['History', renderLog],
  advisory: ['Advisory', renderAdvisory],
  journal: ['Trade Journal', renderJournal],
  ledger: ['Alpha Ledger', renderLedgerPage],
  tax: ['Tax', renderTax],
  integrity: ['Integrity', renderIntegrity]
};

// Active page detection — checks which .page div is actually visible
function _activePage() {
  const activePage = document.querySelector('.page.active');
  if (activePage) {
    const id = activePage.id; // e.g. 'page-cash'
    return id.replace('page-', '');
  }
  return 'dashboard';
}

const _pageToSection = {
  dashboard: 'dash', portfolio: 'port', monthly: 'monthly',
  trades: 'trades', cash: 'cash', cashflow: 'cf',
  goals: 'goals', signals: 'signals', risk: 'risk', bonus: 'bonus', history: 'log',
  advisory: 'advisory', journal: 'journal', ledger: 'ledger', tax: 'tax',
  integrity: 'integrity', settings: null
};

// Debounced renderAll — coalesces multiple calls within same frame
let _renderRAF = null;

function renderAll(force) {
  markDirty(); // renderAll = full render request — always mark all dirty
  if (force) { _renderAllNow(); return; }
  if (_renderRAF) return; // already scheduled
  // Hidden tabs suspend rAF entirely — the scheduled callback never fires, _renderRAF stays
  // set, and every later renderAll() early-returns: the whole pipeline freezes until the tab
  // becomes visible (root cause of the blank deep-link pages, found 2026-07-15). Fall back to
  // setTimeout when hidden so background boots (bookmark middle-click, notification click,
  // automation) still paint. Foreground path stays on rAF.
  if (typeof document !== 'undefined' && document.hidden) {
    _renderRAF = setTimeout(() => { _renderRAF = null; _renderAllNow(); }, 0);
    return;
  }
  _renderRAF = requestAnimationFrame(() => { _renderRAF = null; _renderAllNow(); });
}

/**
 * Hydration gate.
 *
 * Rendering before `load()` finishes paints an EMPTY state: every TRY and EUR holding is
 * converted with no FX rate (18 `toUSD: fxRate missing` warnings per boot), and any render
 * path with an empty-data branch takes it. That is not cosmetic. On 2026-08-03 the timeline's
 * empty branch replaced its own canvas with a placeholder on that first render, destroying
 * <canvas id="chTimeline"> permanently — 51 snapshots in state and no chart on screen, on
 * every load, for an unknown length of time.
 *
 * So: nothing renders until there is something to render. main.js flips this after load()
 * on both the session-restore and login paths.
 */
let _hydrated = false;
function setHydrated() { _hydrated = true; }

function _renderAllNow() {
  if (!_hydrated) return;
  _renderErrors = [];
  const active = _pageToSection[_activePage()];
  // Always render active page first (user sees it)
  if (active && _dirty[active]) {
    const [name, fn] = _renderMap[active];
    safeRender(name, fn);
    _dirty[active] = false;
  }
  // Render remaining dirty sections
  for (const [key, [name, fn]] of Object.entries(_renderMap)) {
    if (_dirty[key]) { safeRender(name, fn); _dirty[key] = false; }
  }
  // Show error badge if any module failed
  const errEl = document.getElementById('renderErrBadge');
  if (errEl) {
    if (_renderErrors.length) {
      errEl.style.display = 'inline-flex';
      errEl.textContent = '⚠ ' + _renderErrors.length + ' render error(s)';
      errEl.title = _renderErrors.map(e => e.module + ': ' + e.error).join('\n');
    } else {
      errEl.style.display = 'none';
    }
  }
  // Fix mobile layout + scroll fades after all renders
  try { renderActionBell(); } catch (e) { /* bell is non-critical */ }
  fixMobileLayout();
  refreshScrollFades();
}

// Targeted render: only render specific sections + mark them clean
function renderOnly(...sections) {
  _renderErrors = [];
  sections.forEach(s => {
    const entry = _renderMap[s];
    if (entry) { safeRender(entry[0], entry[1]); _dirty[s] = false; }
  });
  fixMobileLayout();
  refreshScrollFades();
}

export { safeRender, _dirty, markDirty, renderAll, renderOnly, _renderErrors, _pageToSection, _renderMap, setHydrated };
